import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, RefreshCw, X } from 'lucide-react';
import { CrossCheckClause, formatClauseNumberForDisplay } from '../../types/crossCheck';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import './AnalyzeAgainModal.css';

type AnalyzeAgainReason = 'match' | 'boc' | null;

export type AnalyzeAgainModalVariant = 'mcc' | 'cl-past-negotiations';

interface AnalyzeAgainModalProps {
  clause: CrossCheckClause | null;
  variant?: AnalyzeAgainModalVariant;
  onCancel: () => void;
  onSubmit: (payload: {
    salesAgreementFeedback?: string;
    bankOfChangesFeedback?: string;
    pastNegotiationsFeedback?: string;
  }) => void | Promise<void>;
}

const getTextAlignClass = (text: string | undefined): string => {
  if (!text?.trim()) return '';
  return detectTextDirection(text) === 'rtl' ? 'aa-text-rtl' : 'aa-text-ltr';
};

export const AnalyzeAgainModal: React.FC<AnalyzeAgainModalProps> = ({
  clause,
  variant = 'mcc',
  onCancel,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation('mcc');
  const modalDir = directionForLanguage(i18n.language);
  const [selectedReason, setSelectedReason] = React.useState<AnalyzeAgainReason>(null);
  const [salesAgreementFeedback, setSalesAgreementFeedback] = React.useState('');
  const [bankOfChangesFeedback, setBankOfChangesFeedback] = React.useState('');
  const [pastNegotiationsFeedback, setPastNegotiationsFeedback] = React.useState('');

  React.useEffect(() => {
    if (clause) {
      setSelectedReason(null);
      setSalesAgreementFeedback('');
      setBankOfChangesFeedback('');
      setPastNegotiationsFeedback('');
    }
  }, [clause?.id, variant]);

  const sectionLabel = React.useMemo(() => {
    if (!clause) return t('clauseCard.sections.salesAgreement');
    return clause.documentType === 'appendix_a'
      ? t('clauseCard.sections.appendixA')
      : t('clauseCard.sections.salesAgreement');
  }, [clause, t]);

  if (!clause) return null;

  const clauseLabel = formatClauseNumberForDisplay(clause.clause_number) || clause.id;

  if (variant === 'cl-past-negotiations') {
    const canSubmit = pastNegotiationsFeedback.trim().length > 0;
    return (
      <div
        className="aa-modal-backdrop"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) onCancel();
        }}
      >
        <AnalyzeAgainModalShell onCancel={onCancel} clauseLabel={clauseLabel} modalDir={modalDir}>
          <p className="aa-modal-lead">
            {t('analyzeAgain.pastNegotiationsPrompt')}
          </p>
          <label className="aa-field">
            <span className="aa-field-label">{t('analyzeAgain.pastNegotiationsFeedbackLabel')}</span>
            <textarea
              value={pastNegotiationsFeedback}
              onChange={(e) => setPastNegotiationsFeedback(e.target.value)}
              rows={4}
              autoFocus
              placeholder={t('analyzeAgain.pastNegotiationsPlaceholder')}
              dir={detectTextDirection(pastNegotiationsFeedback)}
              className={`aa-textarea ${getTextAlignClass(pastNegotiationsFeedback)}`}
            />
          </label>
          <div className="aa-modal-actions">
            <button type="button" className="aa-btn-secondary" onClick={onCancel}>
              {t('clauseCard.actions.cancel')}
            </button>
            <button
              type="button"
              className="aa-btn-primary"
              onClick={() => {
                if (!canSubmit) return;
                void onSubmit({ pastNegotiationsFeedback: pastNegotiationsFeedback.trim() });
              }}
              disabled={!canSubmit}
            >
              <RefreshCw className="aa-icon-sm" aria-hidden />
              {t('analyzeAgain.action')}
            </button>
          </div>
        </AnalyzeAgainModalShell>
      </div>
    );
  }

  const activeFeedback = selectedReason === 'match' ? salesAgreementFeedback : bankOfChangesFeedback;
  const canSubmit = selectedReason !== null && activeFeedback.trim().length > 0;
  const matchPlaceholder = t('analyzeAgain.matchPlaceholder', { doc: sectionLabel });
  const bocPlaceholder = t('analyzeAgain.bocPlaceholder');

  const handleSubmit = () => {
    if (!canSubmit || !selectedReason) return;
    void onSubmit(
      selectedReason === 'match'
        ? { salesAgreementFeedback: salesAgreementFeedback.trim() }
        : { bankOfChangesFeedback: bankOfChangesFeedback.trim() },
    );
  };

  return (
    <div
      className="aa-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <AnalyzeAgainModalShell onCancel={onCancel} clauseLabel={clauseLabel} modalDir={modalDir}>
        {selectedReason === null ? (
          <AnalyzeAgainStepPick
            sectionLabel={sectionLabel}
            onCancel={onCancel}
            onSelectReason={setSelectedReason}
          />
        ) : (
          <AnalyzeAgainStepFeedback
            selectedReason={selectedReason}
            sectionLabel={sectionLabel}
            salesAgreementFeedback={salesAgreementFeedback}
            bankOfChangesFeedback={bankOfChangesFeedback}
            matchPlaceholder={matchPlaceholder}
            bocPlaceholder={bocPlaceholder}
            canSubmit={canSubmit}
            onBack={() => setSelectedReason(null)}
            onCancel={onCancel}
            onSubmit={handleSubmit}
            onSalesAgreementFeedbackChange={setSalesAgreementFeedback}
            onBankOfChangesFeedbackChange={setBankOfChangesFeedback}
          />
        )}
      </AnalyzeAgainModalShell>
    </div>
  );
};

function AnalyzeAgainModalShell({
  children,
  onCancel,
  clauseLabel,
  modalDir,
}: {
  children: React.ReactNode;
  onCancel: () => void;
  clauseLabel: string;
  modalDir: 'ltr' | 'rtl';
}) {
  return (
    <div
      className="aa-modal"
      role="dialog"
      aria-modal="true"
      dir={modalDir}
      onClick={(e) => e.stopPropagation()}
    >
      <AnalyzeAgainModalHeader onCancel={onCancel} clauseLabel={clauseLabel} />
      {children}
    </div>
  );
}

function AnalyzeAgainModalHeader({
  onCancel,
  clauseLabel,
}: {
  onCancel: () => void;
  clauseLabel: string;
}) {
  const { t } = useTranslation('mcc');
  return (
    <div className="aa-modal-header">
      <div className="aa-modal-icon-wrap" aria-hidden>
        <RefreshCw className="aa-icon-md aa-icon-blue" />
      </div>
      <div className="aa-modal-header-text">
        <h2 className="aa-modal-title">{t('analyzeAgain.title')}</h2>
        <p className="aa-modal-subtitle">{t('clauseCard.clauseNumber', { number: clauseLabel })}</p>
      </div>
      <button type="button" className="aa-modal-close" onClick={onCancel} aria-label={t('clauseCard.actions.close')}>
        <X className="aa-icon-sm" />
      </button>
    </div>
  );
}

function AnalyzeAgainStepPick({
  sectionLabel,
  onCancel,
  onSelectReason,
}: {
  sectionLabel: string;
  onCancel: () => void;
  onSelectReason: (reason: AnalyzeAgainReason) => void;
}) {
  const { t } = useTranslation('mcc');
  return (
    <>
      <p className="aa-modal-lead">{t('analyzeAgain.prompt')}</p>
      <div className="aa-reason-list">
        <button type="button" className="aa-reason-btn" onClick={() => onSelectReason('match')}>
          {t('analyzeAgain.matchWrong')}
          <span className="aa-reason-hint">
            {t('analyzeAgain.matchWrongHint', { doc: sectionLabel })}
          </span>
        </button>
        <button type="button" className="aa-reason-btn" onClick={() => onSelectReason('boc')}>
          {t('analyzeAgain.analyzeBocAgain')}
          <span className="aa-reason-hint">
            {t('analyzeAgain.analyzeBocAgainHint', { doc: sectionLabel })}
          </span>
        </button>
      </div>
      <AnalyzeAgainModalActionsEnd onCancel={onCancel} />
    </>
  );
}

function AnalyzeAgainModalActionsEnd({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation('mcc');
  return (
    <div className="aa-modal-actions aa-modal-actions--end">
      <button type="button" className="aa-btn-secondary" onClick={onCancel}>
        {t('clauseCard.actions.cancel')}
      </button>
    </div>
  );
}

function AnalyzeAgainStepFeedback({
  selectedReason,
  sectionLabel,
  salesAgreementFeedback,
  bankOfChangesFeedback,
  matchPlaceholder,
  bocPlaceholder,
  canSubmit,
  onBack,
  onCancel,
  onSubmit,
  onSalesAgreementFeedbackChange,
  onBankOfChangesFeedbackChange,
}: {
  selectedReason: Exclude<AnalyzeAgainReason, null>;
  sectionLabel: string;
  salesAgreementFeedback: string;
  bankOfChangesFeedback: string;
  matchPlaceholder: string;
  bocPlaceholder: string;
  canSubmit: boolean;
  onBack: () => void;
  onCancel: () => void;
  onSubmit: () => void;
  onSalesAgreementFeedbackChange: (value: string) => void;
  onBankOfChangesFeedbackChange: (value: string) => void;
}) {
  const { t } = useTranslation('mcc');
  return (
    <>
      <button type="button" className="aa-back-btn" onClick={onBack}>
        <ChevronLeft className="aa-icon-sm" aria-hidden />
        {t('analyzeAgain.back')}
      </button>

      {selectedReason === 'match' ? (
        <label className="aa-field">
          <span className="aa-field-label">{t('analyzeAgain.feedbackLabel', { doc: sectionLabel })}</span>
          <textarea
            value={salesAgreementFeedback}
            onChange={(e) => onSalesAgreementFeedbackChange(e.target.value)}
            rows={4}
            autoFocus
            placeholder={matchPlaceholder}
            dir={detectTextDirection(salesAgreementFeedback)}
            className={`aa-textarea ${getTextAlignClass(salesAgreementFeedback)}`}
          />
        </label>
      ) : (
        <label className="aa-field">
          <span className="aa-field-label">{t('analyzeAgain.bocFeedbackLabel')}</span>
          <textarea
            value={bankOfChangesFeedback}
            onChange={(e) => onBankOfChangesFeedbackChange(e.target.value)}
            rows={4}
            autoFocus
            placeholder={bocPlaceholder}
            dir={detectTextDirection(bankOfChangesFeedback)}
            className={`aa-textarea ${getTextAlignClass(bankOfChangesFeedback)}`}
          />
        </label>
      )}

      <div className="aa-modal-actions">
        <button type="button" className="aa-btn-secondary" onClick={onCancel}>
          {t('clauseCard.actions.cancel')}
        </button>
        <button type="button" className="aa-btn-primary" onClick={onSubmit} disabled={!canSubmit}>
          <RefreshCw className="aa-icon-sm" aria-hidden />
          {t('analyzeAgain.action')}
        </button>
      </div>
    </>
  );
}

export default AnalyzeAgainModal;
